import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Button,
  Card,
  Input,
  Select,
  SelectItem,
  Switch,
} from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, EmptyState } from '../ui';
import {
  tariffRows,
  TARIFF_WEEK_LABELS,
  type TariffBox,
  type TariffConfiguration,
  type TariffWeek,
} from 'common/src/utils/presettlement-tariffs';

const validInput = (value: string) =>
  value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

function TariffAmountInput({
  value,
  onChange,
  onValidityChange,
  fieldKey,
  label,
  placeholder,
  optional = false,
  visibleLabel,
  endContent,
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onValidityChange: (key: string, invalid: boolean) => void;
  fieldKey: string;
  label: string;
  placeholder?: string;
  optional?: boolean;
  visibleLabel?: string;
  endContent?: ReactNode;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  useEffect(() => {
    setText(value === undefined ? '' : String(value));
    onValidityChange(fieldKey, false);
  }, [value, fieldKey, onValidityChange]);
  useEffect(
    () => () => onValidityChange(fieldKey, false),
    [fieldKey, onValidityChange]
  );
  const invalid = !(optional && text === '') && !validInput(text);
  return (
    <Input
      size='sm'
      aria-label={label}
      label={visibleLabel}
      className={visibleLabel ? 'w-40' : undefined}
      isInvalid={invalid}
      type='number'
      min='0'
      step='any'
      value={text}
      placeholder={placeholder}
      endContent={endContent}
      onValueChange={(next) => {
        setText(next);
        const valid = (optional && next === '') || validInput(next);
        onValidityChange(fieldKey, !valid);
        if (valid) onChange(next === '' ? undefined : Number(next));
      }}
    />
  );
}

export default function TariffEditor({
  boxes,
  value,
  onChange,
  disabled = false,
  onValidityChange,
  formatMapping,
}: {
  boxes: TariffBox[];
  value: TariffConfiguration;
  onChange: (value: TariffConfiguration) => void;
  disabled?: boolean;
  formatMapping?: ReactNode;
  onValidityChange: (valid: boolean) => void;
}) {
  const { t } = useTranslation('preSettlements');
  const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>(
    {}
  );
  const markInvalid = useCallback((key: string, invalid: boolean) => {
    setInvalidFields((previous) => {
      if (!!previous[key] === invalid) return previous;
      const next = { ...previous };
      if (invalid) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);
  const invalid = Object.values(invalidFields).some(Boolean);
  useEffect(() => {
    onValidityChange(!invalid);
  }, [invalid, onValidityChange]);
  const [lastWeek, setLastWeek] =
    useState<Exclude<TariffWeek, 'general'>>('dispatch');
  const [search, setSearch] = useState('');
  const [packingFilter, setPackingFilter] = useState('');
  const [bulk, setBulk] = useState('');
  const [page, setPage] = useState(0);
  const rows = useMemo(() => tariffRows(boxes, value), [boxes, value]);
  const filtered = rows.filter(
    (row) =>
      (!packingFilter || (row.packing ?? '__missing__') === packingFilter) &&
      `${row.packaging ?? ''} ${row.packing ?? ''}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase())
  );
  const weeks = [...new Set(filtered.map((row) => row.week))].sort((a, b) =>
    (a ?? '').localeCompare(b ?? '')
  );
  const groups = new Map<string, typeof rows>();
  for (const row of filtered) {
    const key = JSON.stringify([row.packing, row.packaging]);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const allGroups = [...groups.entries()];
  const pageCount = Math.max(1, Math.ceil(allGroups.length / 25));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleGroups = allGroups.slice(
    currentPage * 25,
    (currentPage + 1) * 25
  );
  const missingCount = rows
    .filter(
      (row) =>
        !row.packaging ||
        (value.packing === 'packing' && !row.packing) ||
        (value.week !== 'general' && !row.week)
    )
    .reduce((sum, row) => sum + row.count, 0);
  const weekly = value.week !== 'general';
  const specificCount = rows.filter(
    (row) => value.rates[row.key] !== undefined
  ).length;
  const setRate = (key: string, rate: number | undefined) => {
    const rates = { ...value.rates };
    if (rate === undefined) delete rates[key];
    else rates[key] = rate;
    onChange({ ...value, rates });
  };
  const applyBulk = (rate: number | undefined) => {
    const rates = { ...value.rates };
    for (const row of filtered) {
      if (rate === undefined) delete rates[row.key];
      else rates[row.key] = rate;
    }
    onChange({ ...value, rates });
  };
  const rateInput = (row: (typeof rows)[number]) => {
    const rate = value.rates[row.key];
    const label = [
      row.packing,
      row.packaging ?? t('Sin formato/código'),
      row.week ?? (weekly ? t('Sin fecha') : ''),
      'USD/kg',
    ]
      .filter(Boolean)
      .join(' / ');
    return (
      <div className='min-w-[125px]'>
        <TariffAmountInput
          fieldKey={row.key}
          label={label}
          value={rate}
          placeholder={`${value.defaultRate} (${t('gral')})`}
          optional
          onValidityChange={markInvalid}
          onChange={(rate) => setRate(row.key, rate)}
        />
      </div>
    );
  };

  return (
    <Card shadow='sm' className='min-w-0'>
      <fieldset
        disabled={disabled}
        className='min-w-0 overflow-hidden disabled:opacity-60'
      >
        <div className='flex flex-wrap items-center justify-between gap-4 border-b border-divider px-5 py-5 md:px-7'>
          <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1'>
            <h3 className='font-semibold'>{t('Tarifas USD/kg')}</h3>
            <p className='text-sm text-default-500'>
              {rows.length} {t('combinaciones')} ·{' '}
              {specificCount === 0
                ? t('todas con tarifa general')
                : t('{{count}} con tarifa específica', {
                    count: specificCount,
                  })}
            </p>
          </div>
          <TariffAmountInput
            fieldKey='general'
            label={t('Tarifa general USD/kg')}
            visibleLabel={t('Tarifa general')}
            endContent={
              <span className='pointer-events-none whitespace-nowrap text-xs text-default-400'>
                USD/kg
              </span>
            }
            value={value.defaultRate}
            onValidityChange={markInvalid}
            onChange={(rate) => onChange({ ...value, defaultRate: rate! })}
          />
        </div>
        <div className='flex flex-wrap items-center gap-x-6 gap-y-4 border-b border-divider px-5 py-4 md:px-7'>
          <Select
            size='sm'
            label={t('Tarifa')}
            className='w-52'
            disallowEmptySelection
            isDisabled={disabled}
            selectedKeys={[value.packaging]}
            onChange={(e) =>
              onChange({
                ...value,
                packaging: e.target.value as TariffConfiguration['packaging'],
              })
            }
          >
            <SelectItem key='format' value='format'>
              {t('Por Formato')}
            </SelectItem>
            <SelectItem key='code' value='code'>
              {t('Por Código Embalaje')}
            </SelectItem>
          </Select>
          <Switch
            size='sm'
            classNames={{ label: 'text-sm' }}
            isSelected={value.packing === 'packing'}
            isDisabled={disabled}
            onValueChange={(checked) => {
              setPackingFilter('');
              setPage(0);
              onChange({ ...value, packing: checked ? 'packing' : 'general' });
            }}
          >
            {t('Diferenciar por packing')}
          </Switch>

          <div className='flex flex-wrap items-center gap-4'>
            <Switch
              size='sm'
              classNames={{ label: 'text-sm' }}
              isSelected={weekly}
              isDisabled={disabled}
              onValueChange={(checked) => {
                if (weekly)
                  setLastWeek(value.week as Exclude<TariffWeek, 'general'>);
                setPage(0);
                onChange({ ...value, week: checked ? lastWeek : 'general' });
              }}
            >
              {t('Diferenciar por semana')}
            </Switch>
            {weekly && (
              <Select
                size='sm'
                label={t('Tipo de semana')}
                isDisabled={disabled}
                className='w-52'
                disallowEmptySelection
                selectedKeys={[value.week]}
                onChange={(event) => {
                  const week = event.target.value as Exclude<
                    TariffWeek,
                    'general'
                  >;
                  setLastWeek(week);
                  setPage(0);
                  onChange({ ...value, week });
                }}
              >
                {Object.entries(TARIFF_WEEK_LABELS)
                  .filter(([key]) => key !== 'general')
                  .map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {t(label)}
                    </SelectItem>
                  ))}
              </Select>
            )}
          </div>
        </div>
        {value.packaging === 'format' && formatMapping && (
          <div className='border-b border-divider'>{formatMapping}</div>
        )}
        {missingCount > 0 && (
          <div role='status' className='mx-5 mt-4 md:mx-7'>
            <Alert variant='warning' showIcon>
              {t(
                'Hay filas sin packing, formato/código o fecha. Se muestran como Sin datos y también pueden tener una tarifa específica.'
              )}{' '}
              ({missingCount})
            </Alert>
          </div>
        )}
        <div className='flex flex-wrap items-center gap-3 px-5 py-4 md:px-7'>
          <Input
            size='sm'
            aria-label={t('Buscar formato, código o packing')}
            placeholder={t('Buscar formato, código o packing')}
            className='min-w-[180px] flex-1 md:max-w-md'
            value={search}
            onValueChange={(next) => {
              setSearch(next);
              setPage(0);
            }}
          />
          {value.packing === 'packing' && (
            <Select
              size='sm'
              aria-label={t('Filtrar packing')}
              isDisabled={disabled}
              className='max-w-xs'
              disallowEmptySelection
              selectedKeys={[packingFilter || '__all__']}
              onChange={(e) => {
                setPackingFilter(
                  e.target.value === '__all__' ? '' : e.target.value
                );
                setPage(0);
              }}
            >
              {[
                <SelectItem key='__all__' value='__all__'>
                  {t('Todos los packings')}
                </SelectItem>,
                ...[...new Set(rows.map((row) => row.packing))]
                  .sort()
                  .map((packing) => (
                    <SelectItem
                      key={packing ?? '__missing__'}
                      value={packing ?? '__missing__'}
                    >
                      {packing ?? t('Sin packing')}
                    </SelectItem>
                  )),
              ]}
            </Select>
          )}

          <Input
            size='sm'
            aria-label={t('Tarifa para aplicar en bloque')}
            placeholder='USD/kg'
            className='max-w-[150px]'
            type='number'
            min='0'
            step='0.01'
            value={bulk}
            onValueChange={setBulk}
          />
          <Button
            size='sm'
            variant='flat'
            isDisabled={
              disabled || invalid || !validInput(bulk) || !filtered.length
            }
            onPress={() => applyBulk(Number(bulk))}
          >
            {t('Aplicar a filtradas')} ({filtered.length})
          </Button>
          <Button
            size='sm'
            variant='light'
            isDisabled={disabled || invalid || !filtered.length}
            onPress={() => applyBulk(undefined)}
          >
            {t('Usar general en filtradas')}
          </Button>
        </div>
        <div className='max-h-[65vh] overflow-auto'>
          <table
            aria-label={t('Tarifas USD/kg')}
            className='w-full text-sm border-collapse'
          >
            <thead className='sticky top-0 z-10 bg-default-100 text-xs uppercase tracking-wide text-default-500'>
              <tr>
                {value.packing === 'packing' && (
                  <th className='px-5 py-3 text-left font-medium'>
                    {t('Packing')}
                  </th>
                )}
                <th className='px-5 py-3 text-left font-medium'>
                  {value.packaging === 'format'
                    ? t('Formato')
                    : t('Código Embalaje')}
                </th>
                {weekly ? (
                  weeks.map((week) => (
                    <th
                      key={week ?? '__missing__'}
                      className='px-3 py-3 text-left font-medium whitespace-nowrap'
                    >
                      {week ?? t('Sin fecha')}
                    </th>
                  ))
                ) : (
                  <th className='px-5 py-3 text-left font-medium'>USD/kg</th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map(([key, group]) => (
                <tr
                  key={key}
                  className='border-t border-divider hover:bg-default-100/40'
                >
                  {value.packing === 'packing' && (
                    <td className='px-5 py-2 whitespace-nowrap text-default-500'>
                      {group[0].packing ?? t('Sin packing')}
                    </td>
                  )}
                  <th
                    scope='row'
                    className='px-5 py-2 text-left font-mono font-semibold whitespace-nowrap'
                  >
                    {group[0].packaging ?? t('Sin formato/código')}
                  </th>
                  {weekly ? (
                    weeks.map((week) => {
                      const row = group.find((item) => item.week === week);
                      return (
                        <td
                          key={week ?? '__missing__'}
                          className='px-3 py-2 last:pr-5'
                        >
                          {row ? (
                            rateInput(row)
                          ) : (
                            <span className='text-default-300'>—</span>
                          )}
                        </td>
                      );
                    })
                  ) : (
                    <td className='px-3 py-2 last:pr-5'>
                      {rateInput(group[0])}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <div className='p-6'>
              <EmptyState
                title={t(
                  'Sin combinaciones para estos filtros. La tarifa general se aplicará a nuevos despachos.'
                )}
              />
            </div>
          )}
        </div>
        {pageCount > 1 && (
          <div className='flex justify-end items-center gap-3 px-5 py-3'>
            <Button
              size='sm'
              isDisabled={currentPage === 0}
              onPress={() => setPage(currentPage - 1)}
            >
              {t('Anterior')}
            </Button>
            <span>
              {currentPage + 1} / {pageCount}
            </span>
            <Button
              size='sm'
              isDisabled={currentPage + 1 >= pageCount}
              onPress={() => setPage(currentPage + 1)}
            >
              {t('Siguiente')}
            </Button>
          </div>
        )}
        {invalid && (
          <p role='alert' className='px-5 py-3 text-danger text-sm'>
            {t('Ingresa tarifas válidas mayores o iguales a cero.')}
          </p>
        )}
        <p className='border-t border-divider px-5 py-3 text-xs text-default-500 md:px-7'>
          {t(
            'Las celdas vacías y las combinaciones nuevas usan la tarifa general. Ingresa 0 para una tarifa sin costo.'
          )}
        </p>
      </fieldset>
    </Card>
  );
}
